import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { useTriggerComputedSync } from '@framework/form/hooks/useTriggerComputedSync.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { Space } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { hasRichTextAdvanced } from '../../../../../inputs/rich-text-advanced/hasRichTextAdvanced.ts';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { AdvancedSelectModalBase } from '../advanced-select-common/components/AdvancedSelectModalBase.tsx';
import { ShowOnlySelectedCheckbox } from '../advanced-select-common/components/ShowOnlySelectedCheckbox.tsx';
import { useAdvancedSelectModal } from '../advanced-select-common/hooks/useAdvancedSelectModal.ts';
import { useAdvancedSelectModalLabels } from '../advanced-select-common/hooks/useAdvancedSelectModalLabels.ts';
import { getDinamicButtonProps } from '../advanced-select-common/utils/getDinamicButtonProps.tsx';
import { applyMultiSelectFormValues } from '../multi-select/applyMultiSelectFormValues.ts';
import { multiSelectOptionsObjectName } from '../multi-select/getMultiSelectOptionsObjectName.ts';
import { AdvancedMultiSelectTableData } from './AdvancedMultiSelectTableData.tsx';
import { type IAdvancedMultiSelectWidget } from './IAdvancedMultiSelectWidget.ts';

export interface IAdvancedMultiSelectWidgetModalProps
  extends IWidgetComponentProps<IAdvancedMultiSelectWidget> {
  currentOption: Record<string, boolean> | null;
}

/**
 * Componente modale per la selezione multipla avanzata
 */
export function AdvancedMultiSelectWidgetModal(
  props: IAdvancedMultiSelectWidgetModalProps,
) {
  const { widget, currentOption, readonly } = props;
  const { prefix } = useWidgetScopeContext();
  const setValue = useFormSetValue();
  const triggerSync = useTriggerComputedSync();

  const objectFieldName = useMemo(
    () => `${prefix}${multiSelectOptionsObjectName(widget.code)}`,
    [prefix, widget.code],
  );

  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const isOptionSelected = useMemo(
    () => currentOption != null && Object.values(currentOption).includes(true),
    [currentOption],
  );

  const { buttonText } = useMemo(
    () =>
      getDinamicButtonProps(
        widget.buttonText,
        widget.buttonTextAfter,
        isOptionSelected,
      ),
    [widget, isOptionSelected],
  );

  // Ogni chiave nota viene azzerata a false prima di applicare lo snapshot:
  // passare un oggetto vuoto lascerebbe i valori precedenti invariati in RHF.
  const restoreMultiState = useCallback(
    (snapshot: Record<string, boolean> | null, shouldDirty = false) => {
      const clearedValues = Object.fromEntries(
        Object.keys(currentOption ?? {}).map(key => [key, false]),
      );
      applyMultiSelectFormValues({
        setValue,
        triggerSync,
        objectFieldName,
        values: { ...clearedValues, ...snapshot },
        shouldDirty,
      });
    },
    [currentOption, objectFieldName, setValue, triggerSync],
  );

  const clearMultiSelectValues = useCallback(
    () => restoreMultiState(null, true),
    [restoreMultiState],
  );

  const {
    open,
    handleOpen,
    handleConfirm,
    handleCancel,
    handleClearSelection,
  } = useAdvancedSelectModal(
    currentOption,
    restoreMultiState,
    clearMultiSelectValues,
  );

  const { confirmText, cancelText, clearSelectionText } =
    useAdvancedSelectModalLabels({
      buttonText: widget.buttonText,
      cancelButtonText: widget.cancelButtonText,
    });

  return (
    <AdvancedSelectModalBase
      readonly={readonly}
      buttonText={buttonText}
      open={open}
      onOpen={handleOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      onClearSelection={handleClearSelection}
      showClearSelection={isOptionSelected && !readonly}
      labels={{
        confirm: confirmText,
        cancel: cancelText,
        clearSelection: clearSelectionText,
      }}
      title={
        <Space size="large" align="center">
          <RichTextAdvancedRender
            text={
              hasRichTextAdvanced(widget.modalTitle)
                ? widget.modalTitle
                : t(
                    'widgets.advanced-multi-select.modal-select-option-title',
                    'Seleziona Opzioni',
                  )
            }
          />
          <ShowOnlySelectedCheckbox
            checked={showOnlySelected}
            onChange={setShowOnlySelected}
          />
        </Space>
      }
    >
      <AdvancedMultiSelectTableData
        {...props}
        selectedOptions={currentOption}
        showOnlySelected={showOnlySelected}
      />
    </AdvancedSelectModalBase>
  );
}
